
import { defineComponent, PropType, computed, ref } from "vue";
import { getStakeColor } from "./utils/stakeColors";

export default defineComponent({
  name: "StakesSummary",
  props: {
    stakeDistribution: {
      type: Object as PropType<Record<string, number>>,
      required: true,
    },
  },
  setup(props) {
    const sortBy = ref<"stake" | "count">("stake");
    const sortOrder = ref<"asc" | "desc">("desc");

    const setSort = (key: "stake" | "count", order: "asc" | "desc") => {
      sortBy.value = key;
      sortOrder.value = order;
    };

    const parsedItems = computed(() =>
      Object.entries(props.stakeDistribution).map(([stake, count]) => ({
        stake,
        count,
        stakeValue: parseFloat(stake.replace("%", "")) || 0,
      }))
    );

    // Numerically sorted stakes for consistent color mapping across components
    const sortedUniqueStakes = computed(() => {
      return parsedItems.value
        .map((item) => item.stake)
        .sort((a, b) => parseFloat(a) - parseFloat(b));
    });

    // Helper to get consistent color using numerically sorted stakes
    const getItemColor = (stake: string) => {
      return getStakeColor(stake, sortedUniqueStakes.value, "solid");
    };

    const maxCount = computed(() => {
      return Math.max(...parsedItems.value.map((i) => i.count), 0);
    });

    const getPercentage = (count: number) => {
      if (maxCount.value === 0) return 0;
      return (count / maxCount.value) * 100;
    };

    const sortedStakes = computed(() => {
      const items = [...parsedItems.value];

      return items.sort((a, b) => {
        let comparison = 0;
        if (sortBy.value === "stake") {
          comparison = a.stakeValue - b.stakeValue;
        } else {
          comparison = a.count - b.count;
        }
        return sortOrder.value === "asc" ? comparison : -comparison;
      });
    });

    const leftColumnItems = computed(() => {
      const mid = Math.ceil(sortedStakes.value.length / 2);
      return sortedStakes.value.slice(0, mid);
    });

    const rightColumnItems = computed(() => {
      const mid = Math.ceil(sortedStakes.value.length / 2);
      return sortedStakes.value.slice(mid);
    });

    return {
      sortBy,
      sortOrder,
      setSort,
      sortedStakes,
      getItemColor,
      getPercentage,
      leftColumnItems,
      rightColumnItems,
    };
  },
});
